import { useMemo, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { lookFamily, newId, SETTING_LOOK_CODES, TASK_TYPE_TAGS, type SettingLookCode, type SettingLookFamily } from '@gk/contracts'
import { useAreas } from '../../data/geo.ts'
import { useRpc } from '../../data/people.ts'
import { useTaskTypes, useTemplates, useTemplateVersions, type ParamDef, type TaskTypeRow } from '../../data/plan.ts'
import {
  blankParam, blankTaskType, buildTaskTypeArchive, buildTaskTypeDefine, buildTaskTypeUpdate, CODE_RE, draftOfTaskType, familyOfRows, langOf, MAX_CHOICES, MAX_SETTINGS, moveItem, newChoiceRow, splitChoices, suggestCode, suggestKey,
  tagLabel, TASK_TAG_TEXT, taskTypeProblems, usedParamKeys, type ChoiceRow, type ParamDraft, type TaskTypeDraft,
} from '../../data/planSetup.ts'
import { Banner, Field, Pill } from '../../ui/components.tsx'
import { useWorkshopAction } from '../../ui/hooks.ts'
import { LookPicker } from '../../ui/looks/LookPicker.tsx'
import { LookPicture } from '../../ui/looks/LookPicture.tsx'
import { CheckRow, ConfirmAction, Feedback, Problems, useSetupAccess } from './shared.tsx'

interface AreaRules { types: Array<{ code: string; label: { fr: string; en: string }; sort: number }> }
type Access = ReturnType<typeof useSetupAccess>
type Action = ReturnType<typeof useWorkshopAction>
const PARAM_KINDS = ['text', 'number', 'choice'] as const

/** Task types: the kinds of work the club does, where each one applies (a list of area kinds, none = anywhere) and which settings its job card shows. */
export function TaskTypesTab() {
  const { t, i18n } = useTranslation()
  const lang = langOf(i18n.language)
  const tts = useTaskTypes(); const templates = useTemplates(); const tvs = useTemplateVersions(); const areas = useAreas()
  const rules = useRpc<AreaRules>('area_rules', {})
  const access = useSetupAccess('template.publish')
  const w = useWorkshopAction()
  const [editing, setEditing] = useState<{ tt: TaskTypeRow | null } | null>(null)
  const kindCodes = useMemo(() => (rules.data ? rules.data.types.map((x) => x.code) : [...new Set((areas ?? []).map((a) => a.area_type_code))].sort()), [rules.data, areas])
  const sorted = useMemo(() => [...(tts ?? [])].sort((a, b) => Number(!!a.archived_at) - Number(!!b.archived_at) || a.label[lang].localeCompare(b.label[lang], lang)), [tts, lang])
  if (!tts || !templates || !tvs) return <p>{t('common.loading')}</p>
  const kindLabel = (code: string): string => t(`areaType.${code}`, { defaultValue: code })

  if (editing) {
    return <TaskTypeForm key={editing.tt?.id ?? 'new'} tt={editing.tt} kindCodes={kindCodes} kindLabel={kindLabel} used={editing.tt ? usedParamKeys(editing.tt.code, templates, tvs) : new Set()}
      w={w} access={access} onClose={() => setEditing(null)} />
  }
  const settingNames = (defs: ParamDef[]) => defs.map((d) => d.label[lang] + (d.unit ? ` (${d.unit})` : '')).join(' · ')
  const archive = (tt: TaskTypeRow, archived: boolean) => w.run('task_type.update@2', buildTaskTypeArchive(tt, archived), archived ? 'planSetup.types.archivedDone' : 'planSetup.types.unarchivedDone', { aggregateId: tt.id })
  return (
    <section className="flex flex-col gap-4" data-testid="tt-list-view">
      <div className="flex flex-col items-start gap-3">
        <div className="min-w-0">
          <h2 className="text-xl font-bold">{t('planSetup.types.title')}</h2>
          <p className="text-sm opacity-75">{t('planSetup.types.help')}</p>
        </div>
        {access.permitted && <button className="btn btn-primary !min-h-11" disabled={!access.canSave} onClick={() => { w.reset(); setEditing({ tt: null }) }} data-testid="tt-new">{t('planSetup.types.new')}</button>}
      </div>
      {!access.permitted && <Banner tone="info">{t('planSetup.notAllowed')}</Banner>}
      <Feedback error={w.error} ok={w.ok} pending={w.pending} />
      {sorted.length === 0 && <p className="opacity-70" data-testid="tt-none">{t('planSetup.types.none')}</p>}
      <ul className="flex flex-col gap-3" data-testid="tt-list">
        {sorted.map((tt) => (
          <li key={tt.id} className="card flex flex-col gap-2" data-testid="tt-row" data-code={tt.code}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-lg font-bold">{tt.label[lang]}</p>
                <p className="meta font-mono">{tt.code}</p>
              </div>
              {tt.archived_at && <Pill tone="gray">{t('planSetup.archived')}</Pill>}
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="tt-kinds">
              {tt.applicable_area_types.length === 0 ? <Pill tone="gray">{t('planSetup.types.anyArea')}</Pill> : tt.applicable_area_types.map((c) => <Pill key={c} tone="green">{kindLabel(c)}</Pill>)}
            </div>
            <p className="text-sm opacity-80"><span data-testid="tt-settings-count">{t('planSetup.types.settings', { count: tt.param_defs.length })}</span>{tt.param_defs.length > 0 && <> : {settingNames(tt.param_defs)}</>}</p>
            {(tt.tags ?? []).length > 0 && <p className="text-sm" data-testid="tt-tags">{t('planSetup.types.tagsSummary', { tags: (tt.tags ?? []).map((x) => tagLabel(x, t)).join(' · ') })}</p>}
            {access.permitted && (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                <button className="btn !min-h-11" disabled={!access.canSave} onClick={() => { w.reset(); setEditing({ tt }) }} data-testid="tt-edit">{t('planSetup.edit')}</button>
                {tt.archived_at
                  ? <ConfirmAction id="tt-unarchive" label={t('planSetup.types.unarchive')} message={t('planSetup.types.unarchiveAsk')} confirmLabel={t('planSetup.types.unarchiveYes')} busy={w.busy} disabled={!access.canSave} onConfirm={() => archive(tt, false)} />
                  : <ConfirmAction id="tt-archive" label={t('planSetup.types.archive')} message={t('planSetup.types.archiveAsk')} confirmLabel={t('planSetup.types.archiveYes')} busy={w.busy} disabled={!access.canSave} danger onConfirm={() => archive(tt, true)} />}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

/** `tt` null = a new task type. Everything it needs is passed in loaded (a form that seeds its state from a live query would start empty). */
function TaskTypeForm({ tt, kindCodes, kindLabel, used, w, access, onClose }: {
  tt: TaskTypeRow | null; kindCodes: string[]; kindLabel: (code: string) => string; used: Set<string>; w: Action; access: Access; onClose: () => void
}) {
  const { t, i18n } = useTranslation()
  const lang = langOf(i18n.language)
  const creating = tt === null
  const id = useRef(newId())
  const [d, setD] = useState<TaskTypeDraft>(() => (tt ? draftOfTaskType(tt) : blankTaskType()))
  const [codeTouched, setCodeTouched] = useState(false)
  const kinds = useMemo(() => [...new Set([...kindCodes, ...(tt?.applicable_area_types ?? [])])], [kindCodes, tt])
  const problems = taskTypeProblems(d, creating)
  const update = tt ? buildTaskTypeUpdate(tt, d) : null
  const nothingToSave = !creating && update === null

  const setParam = (i: number, p: Partial<ParamDraft>) => setD((x) => ({ ...x, params: x.params.map((q, j) => (j === i ? { ...q, ...p } : q)) }))
  const uniqueKey = (base: string, except: number): string => {
    if (!base) return ''
    const taken = new Set(d.params.filter((_, j) => j !== except).map((p) => p.key))
    let k = base
    for (let n = 2; taken.has(k); n++) k = `${base.slice(0, 28)}_${n}`
    return k
  }
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (problems.length > 0 || nothingToSave) return
    let ok: boolean
    // M25: update@3 (the pictures of the choices) and define@2 (the tags and the pictures in the same command: no second one after it)
    if (tt) ok = update !== null && await w.run('task_type.update@3', update, 'planSetup.types.saved', { aggregateId: tt.id })
    else ok = await w.run('task_type.define@2', buildTaskTypeDefine(id.current, d), 'planSetup.types.created', { aggregateId: id.current })
    if (ok) onClose()
  }

  return (
    <form noValidate onSubmit={(e) => void submit(e)} className="flex flex-col gap-4" data-testid="tt-form">
      <button type="button" className="self-start text-sm font-semibold underline" onClick={onClose} data-testid="tt-cancel">← {t('common.back')}</button>
      <h2 className="text-xl font-bold">{tt ? t('planSetup.types.editTitle', { label: tt.label[lang] }) : t('planSetup.types.newTitle')}</h2>

      <Field label={t('planSetup.types.labelFr')} htmlFor="tt-label-fr">
        <input id="tt-label-fr" className="input" maxLength={120} value={d.fr} data-testid="tt-label-fr"
          onChange={(e) => setD({ ...d, fr: e.target.value, ...(creating && !codeTouched ? { code: suggestCode(e.target.value) } : {}) })} />
      </Field>
      <Field label={t('planSetup.types.labelEn')} htmlFor="tt-label-en"><input id="tt-label-en" className="input" maxLength={120} value={d.en} data-testid="tt-label-en" onChange={(e) => setD({ ...d, en: e.target.value })} /></Field>
      <Field label={t('planSetup.types.code')} hint={t(creating ? 'planSetup.types.codeHelp' : 'planSetup.types.codeFixed')} htmlFor="tt-code"
        error={creating && d.code.trim() !== '' && !CODE_RE.test(d.code.trim()) ? t('planSetup.err.CODE_INVALID') : null}>
        <input id="tt-code" className="input font-mono" maxLength={41} value={d.code} disabled={!creating} autoCapitalize="none" autoCorrect="off" spellCheck={false} data-testid="tt-code"
          onChange={(e) => { setCodeTouched(true); setD({ ...d, code: e.target.value.toLowerCase() }) }} />
      </Field>

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-sm font-semibold">{t('planSetup.types.areaKinds')}</legend>
        <p className="meta">{t('planSetup.types.areaKindsHelp')}</p>
        <div className="flex flex-wrap gap-2" data-testid="tt-kinds-editor">
          {kinds.map((c) => (
            <CheckRow key={c} checked={d.areaTypes.includes(c)} testId={`tt-area-${c}`} onChange={(on) => setD({ ...d, areaTypes: on ? [...d.areaTypes, c] : d.areaTypes.filter((x) => x !== c) })}>{kindLabel(c)}</CheckRow>
          ))}
        </div>
      </fieldset>

      {/* M16: the words the app reads on a type (a screen of its own on its jobs); a word the club put another way stays on the row untouched */}
      <fieldset className="flex flex-col gap-2" data-testid="tt-tags-editor">
        <legend className="mb-1 text-sm font-semibold">{t('planSetup.types.tagsTitle')}</legend>
        <p className="meta">{t('planSetup.types.tagsHelp')}</p>
        {TASK_TYPE_TAGS.map((tag) => (
          <div key={tag} className="flex flex-col gap-1">
            <CheckRow checked={d.tags.includes(tag)} testId={`tt-tag-${tag}`} onChange={(on) => setD({ ...d, tags: on ? [...d.tags, tag] : d.tags.filter((x) => x !== tag) })}>{t(TASK_TAG_TEXT[tag].label)}</CheckRow>
            <p className="meta pl-3">{t(TASK_TAG_TEXT[tag].help)}</p>
          </div>
        ))}
      </fieldset>

      <section className="flex flex-col gap-3" data-testid="tt-params">
        <h3 className="text-lg font-bold">{t('planSetup.types.settingsTitle')}</h3>
        <p className="meta">{t('planSetup.types.settingsHelp')}</p>
        {d.params.map((p, i) => {
          const inUse = p.locked && used.has(p.key)
          return (
            <div key={i} className="card flex flex-col gap-3" data-testid="tt-param">
              <div className="flex items-center justify-between gap-2">
                <h4 className="font-bold">{t('planSetup.types.settingN', { n: i + 1 })}</h4>
                <button type="button" className="btn !min-h-11 !px-4" disabled={inUse} onClick={() => setD({ ...d, params: d.params.filter((_, j) => j !== i) })} data-testid={`tt-param-remove-${i}`}>{t('common.remove')}</button>
              </div>
              {inUse && <p className="meta">{t('planSetup.types.settingInUse')}</p>}
              <Field label={t('planSetup.types.labelFr')} htmlFor={`tt-param-fr-${i}`}>
                <input id={`tt-param-fr-${i}`} className="input" maxLength={120} value={p.fr} data-testid={`tt-param-fr-${i}`}
                  onChange={(e) => setParam(i, { fr: e.target.value, ...(!p.keyTouched && !p.locked ? { key: uniqueKey(suggestKey(e.target.value), i) } : {}) })} />
              </Field>
              <Field label={t('planSetup.types.labelEn')} htmlFor={`tt-param-en-${i}`}><input id={`tt-param-en-${i}`} className="input" maxLength={120} value={p.en} data-testid={`tt-param-en-${i}`} onChange={(e) => setParam(i, { en: e.target.value })} /></Field>
              <Field label={t('planSetup.types.settingKey')} hint={t('planSetup.types.settingKeyHelp')} htmlFor={`tt-param-key-${i}`}>
                <input id={`tt-param-key-${i}`} className="input font-mono" maxLength={31} value={p.key} readOnly={p.locked} autoCapitalize="none" autoCorrect="off" spellCheck={false} data-testid={`tt-param-key-${i}`}
                  onChange={(e) => setParam(i, { key: e.target.value.toLowerCase(), keyTouched: true })} />
              </Field>
              <Field label={t('planSetup.types.settingKind')} htmlFor={`tt-param-kind-${i}`}>
                <select id={`tt-param-kind-${i}`} className="input" value={p.kind} disabled={p.locked} data-testid={`tt-param-kind-${i}`} onChange={(e) => setParam(i, { kind: e.target.value as ParamDef['kind'] })}>
                  {PARAM_KINDS.map((k) => <option key={k} value={k}>{t(`planSetup.types.kind.${k}`)}</option>)}
                </select>
              </Field>
              {p.kind === 'number' && (
                <Field label={t('planSetup.types.settingUnit')} hint={t('planSetup.types.settingUnitHelp')} htmlFor={`tt-param-unit-${i}`}>
                  <input id={`tt-param-unit-${i}`} className="input" maxLength={12} value={p.unit} data-testid={`tt-param-unit-${i}`} onChange={(e) => setParam(i, { unit: e.target.value })} />
                </Field>
              )}
              {p.kind === 'choice' && <ChoiceRows pi={i} rows={p.choices} onChange={(choices) => setParam(i, { choices })} />}
            </div>
          )
        })}
        <button type="button" className="btn" disabled={d.params.length >= MAX_SETTINGS} onClick={() => setD({ ...d, params: [...d.params, blankParam()] })} data-testid="tt-param-add">{t('planSetup.types.addSetting')}</button>
      </section>

      <Problems codes={problems} />
      <Feedback error={w.error} pending={w.pending} />
      <button className="btn btn-primary" disabled={w.busy || !access.canSave || problems.length > 0 || nothingToSave} data-testid="tt-save">{w.busy ? t('common.saving') : creating ? t('planSetup.types.create') : t('common.save')}</button>
      {!access.online && <p className="meta">{t('planSetup.needsNetwork')}</p>}
    </form>
  )
}

const FAMILIES: readonly SettingLookFamily[] = ['dir', 'pat', 'lap', 'tool']

/** M25-S (docs/M25.md E): the choices of a choice setting as ROWS, in the club's order. A row holds the word, an « Image » button that opens the library's tiles
 *  by family (and « Sans image »), Monter / Descendre and Retirer (56 px each), « Ajouter un choix » under them. The picture hangs on the row, not on the word:
 *  editing a word keeps its picture, and the draft is keyed by the word only when it is sent (`looksOfDraft`). One family of pictures per setting: while another
 *  row has a picture, this row's library offers that family only. A list typed or pasted with commas becomes rows (`splitChoices`). */
function ChoiceRows({ pi, rows, onChange }: { pi: number; rows: ChoiceRow[]; onChange: (rows: ChoiceRow[]) => void }) {
  const { t } = useTranslation()
  const [openId, setOpenId] = useState<string | null>(null)
  const set = (id: string, patch: Partial<ChoiceRow>) => onChange(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)))
  const typed = (i: number, value: string) => {
    const row = rows[i]!
    if (!/[,;\n]/.test(value)) { set(row.id, { text: value }); return }
    const [first = '', ...more] = splitChoices(value)
    onChange([...rows.slice(0, i), { ...row, text: first }, ...more.map((w) => newChoiceRow(w)), ...rows.slice(i + 1)].slice(0, MAX_CHOICES + 5))
  }
  const btn = 'btn !min-h-14 !px-3'
  return (
    <div className="flex flex-col gap-2" data-testid={`tt-choices-${pi}`}>
      <p className="text-sm font-semibold">{t('planSetup.types.settingChoices')}</p>
      <p className="meta">{t('planSetup.types.settingChoicesHelp')}</p>
      {rows.some((r) => r.look) && <p className="meta">{t('planSetup.types.lookOneFamily')}</p>}
      {rows.map((r, i) => {
        const name = r.look ? t(`looks.${r.look}`) : t('planSetup.types.choiceNoLook')
        const others = familyOfRows(rows.filter((x) => x.id !== r.id))
        const open = openId === r.id
        return (
          <div key={r.id} className="flex flex-col gap-2 rounded-xl border-2 border-slate-300 p-2 dark:border-night-line" data-testid="tt-choice" data-index={i}>
            <input className="input" maxLength={60} value={r.text} autoCapitalize="none" placeholder={t('planSetup.types.choiceN', { n: i + 1 })} aria-label={t('planSetup.types.choiceWord', { n: i + 1 })}
              id={`tt-choice-${pi}-${i}`} data-testid={`tt-choice-${pi}-${i}`} onChange={(e) => typed(i, e.target.value)} />
            <div className="flex flex-wrap gap-2">
              <button type="button" className={`${btn} inline-flex items-center gap-2`} aria-expanded={open} aria-label={t('planSetup.types.choiceLookOf', { n: i + 1, name })}
                onClick={() => setOpenId(open ? null : r.id)} data-testid={`tt-choice-look-${pi}-${i}`}>
                {r.look && <LookPicture code={r.look} frame={lookFamily(r.look) === 'pat' ? 'strip' : 'round'} size="picto" className="size-8 shrink-0 text-brand dark:text-fairway" />}
                <span>{t('planSetup.types.choiceLook')}</span>
              </button>
              <button type="button" className={btn} disabled={i === 0} aria-label={t('planSetup.moveUpOf', { name: r.text || t('planSetup.types.choiceN', { n: i + 1 }) })}
                onClick={() => onChange(moveItem(rows, i, -1))} data-testid={`tt-choice-up-${pi}-${i}`}>{t('planSetup.moveUp')}</button>
              <button type="button" className={btn} disabled={i === rows.length - 1} aria-label={t('planSetup.moveDownOf', { name: r.text || t('planSetup.types.choiceN', { n: i + 1 }) })}
                onClick={() => onChange(moveItem(rows, i, 1))} data-testid={`tt-choice-down-${pi}-${i}`}>{t('planSetup.moveDown')}</button>
              <button type="button" className={btn} onClick={() => onChange(rows.filter((x) => x.id !== r.id))} data-testid={`tt-choice-remove-${pi}-${i}`}>{t('common.remove')}</button>
            </div>
            {open && (
              <div className="flex flex-col gap-3" data-testid={`tt-looks-${pi}-${i}`}>
                <p className="font-semibold">{t('planSetup.types.lookPickTitle', { n: i + 1 })}</p>
                {FAMILIES.filter((f) => !others || others === f).map((f) => (
                  <LookPicker key={f} legend={t(`planSetup.types.lookFamily.${f}`)} layout="tiles" frame={f === 'pat' ? 'strip' : 'round'} value={r.look && lookFamily(r.look) === f ? r.look : null}
                    options={SETTING_LOOK_CODES.filter((c) => lookFamily(c) === f).map((c: SettingLookCode) => ({ value: c, label: t(`looks.${c}`), look: c }))}
                    onChange={(code) => { set(r.id, { look: (code as SettingLookCode | null) ?? null }); setOpenId(null) }} />
                ))}
                <div className="flex flex-wrap gap-2">
                  <button type="button" className={btn} onClick={() => { set(r.id, { look: null }); setOpenId(null) }} data-testid={`tt-look-none-${pi}-${i}`}>{t('planSetup.types.choiceNoLook')}</button>
                  <button type="button" className={btn} onClick={() => setOpenId(null)} data-testid={`tt-look-close-${pi}-${i}`}>{t('planSetup.types.choiceLookClose')}</button>
                </div>
              </div>
            )}
          </div>
        )
      })}
      <button type="button" className={`${btn} self-start`} disabled={rows.length >= MAX_CHOICES} onClick={() => onChange([...rows, newChoiceRow()])} data-testid={`tt-choice-add-${pi}`}>{t('planSetup.types.choiceAdd')}</button>
    </div>
  )
}
